import React, { useReducer, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  STEPS,
  estadoInicial,
  podeAvancar,
  precisaCriarRascunho,
  wizardReducer,
} from "./wizardMachine";
import StepCredenciais from "./StepCredenciais";
import StepNumero from "./StepNumero";
import StepWebhook from "./StepWebhook";
import StepIdentificacao from "./StepIdentificacao";
import StepConclusao from "./StepConclusao";

const WhatsAppOficialWizard = ({ open, onClose, onConcluido }) => {
  const [state, dispatch] = useReducer(wizardReducer, estadoInicial);
  const [carregando, setCarregando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);

  const numeroEscolhido = () => {
    if (!state.descoberta) return null;
    for (const b of state.descoberta.businesses) {
      for (const w of b.wabas) {
        const n = w.phone_numbers.find((p) => p.phone_number_id === state.phoneNumberId);
        if (n) return { business: b, waba: w, numero: n };
      }
    }
    return null;
  };

  const avanca = async () => {
    setCarregando(true);
    try {
      if (STEPS[state.step] === "credenciais") {
        const { data } = await api.post("/whatsapp/oficial/discover", {
          token: state.token,
          business_id: state.businessIdManual || undefined,
        });
        dispatch({ type: "SET_DESCOBERTA", resultado: data });
        if (data.precisaBusinessIdManual) {
          setCarregando(false);
          return;
        }
      }

      if (STEPS[state.step] === "numero") {
        const escolha = numeroEscolhido();
        // Reaproveita o rascunho existente quando o numero nao mudou (usuario
        // voltou um step). Recriar aqui colidiria com o proprio rascunho.
        if (precisaCriarRascunho(state, escolha.numero.phone_number_id)) {
          const { data } = await api.post("/whatsapp/oficial/draft", {
            token: state.token,
            business_id: escolha.business.business_id,
            waba_id: escolha.waba.waba_id,
            phone_number_id: escolha.numero.phone_number_id,
            phone_number: escolha.numero.display_phone_number,
            verified_name: escolha.numero.verified_name,
          });
          dispatch({
            type: "SET_RASCUNHO",
            id: data.id,
            phoneNumberId: escolha.numero.phone_number_id,
            waba_webhook: data.waba_webhook,
            verify_token: data.verify_token,
          });
          dispatch({
            type: "SET_CAMPO",
            campo: "nome",
            valor:
              escolha.numero.verified_name || escolha.numero.display_phone_number,
          });
        }
      }

      if (STEPS[state.step] === "identificacao") {
        const { data } = await api.post(`/whatsapp/oficial/${state.whatsappId}/finish`, {
          name: state.nome,
          queueIds: state.queueIds,
          vertical: state.vertical || undefined,
          statusWebhook: state.webhookVerificado ? "verified" : "unverified",
        });
        setResultadoTeste(null);
        api
          .post(`/whatsapp/oficial/${state.whatsappId}/test-message`)
          .then(() => setResultadoTeste({ ok: true }))
          .catch((err) =>
            setResultadoTeste({
              ok: false,
              erro: err?.response?.data?.error || err.message,
            })
          );
        dispatch({ type: "SET_CAMPO", campo: "criado", valor: data });
      }

      dispatch({ type: "AVANCAR" });
    } catch (err) {
      toastError(err);
    }
    setCarregando(false);
  };

  const renderStep = () => {
    switch (STEPS[state.step]) {
      case "credenciais":
        return <StepCredenciais state={state} dispatch={dispatch} />;
      case "numero":
        return <StepNumero state={state} dispatch={dispatch} />;
      case "webhook":
        return <StepWebhook state={state} dispatch={dispatch} />;
      case "identificacao":
        return <StepIdentificacao state={state} dispatch={dispatch} />;
      default:
        return (
          <StepConclusao
            state={state}
            resultadoTeste={resultadoTeste}
            onAbrirConfiguracoes={() => onConcluido(state.criado)}
            onFechar={onClose}
          />
        );
    }
  };

  const ultimo = STEPS[state.step] === "conclusao";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">{i18n.t("whatsappOficialWizard.title")}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={state.step} alternativeLabel>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{i18n.t(`whatsappOficialWizard.steps.${s}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div style={{ marginTop: 16 }}>{renderStep()}</div>
      </DialogContent>
      {!ultimo && (
        <DialogActions>
          <Button onClick={onClose} disabled={carregando}>
            {i18n.t("whatsappOficialWizard.botoes.cancelar")}
          </Button>
          <Button
            onClick={() => dispatch({ type: "VOLTAR" })}
            disabled={carregando || state.step === 0}
          >
            {i18n.t("whatsappOficialWizard.botoes.voltar")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={avanca}
            disabled={carregando || !podeAvancar(state)}
          >
            {STEPS[state.step] === "identificacao"
              ? i18n.t("whatsappOficialWizard.botoes.concluir")
              : i18n.t("whatsappOficialWizard.botoes.continuar")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default WhatsAppOficialWizard;
